<template>
    <div class="block">
        <h1 class="title is-5">Delivery Statistics</h1>

        <!-- Version 3: location-aware count queries -->
        <template v-if="currentFD.statsVersion === 3">
            <template v-if="currentFD.status !== 'CLOSED'">
                <div class="mb-3" style="display: flex; align-items: center; gap: 1rem;">
                    <button
                        class="button is-small is-info"
                        :disabled="deliveryStatsLoading || refreshCooldown > 0"
                        @click="refreshStats"
                    >
                        {{ refreshCooldown > 0 ? `Refresh (${refreshCooldown}s)` : 'Refresh Stats' }}
                    </button>
                    <span v-if="deliveryStatsLoading" class="has-text-grey">Loading…</span>
                </div>
            </template>

            <div v-if="activeStats === null && !deliveryStatsLoading" class="notification is-warning is-light">
                Location breakdown unavailable — regenerate deliveries to enable accurate stats.
            </div>

            <div v-else-if="activeStats" style="overflow-x: auto;">
            <table class="table is-fullwidth is-bordered is-striped is-narrow">
                <thead>
                    <tr>
                        <th>Delivery</th>
                        <th>Total</th>
                        <th title="Done / Delivered"><i class="fas fa-check has-text-success"></i></th>
                        <th title="Not Delivered"><i class="fas fa-xmark has-text-danger"></i></th>
                        <th title="Pending"><i class="fas fa-clock"></i></th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Self Pickup</td>
                        <td>{{ activeStats.selfRedhill.total }}</td>
                        <td>{{ activeStats.selfRedhill.done }}</td>
                        <td>—</td>
                        <td>{{ activeStats.selfRedhill.pending }}</td>
                    </tr>
                    <tr>
                        <td>Walking</td>
                        <td>{{ activeStats.walking.total }}</td>
                        <td>{{ activeStats.walking.delivered }}</td>
                        <td>{{ activeStats.walking.notDelivered }}</td>
                        <td>{{ activeStats.walking.pending }}</td>
                    </tr>
                    <tr>
                        <td>Driving</td>
                        <td>{{ activeStats.driving.total }}</td>
                        <td>{{ activeStats.driving.delivered }}</td>
                        <td>{{ activeStats.driving.notDelivered }}</td>
                        <td>{{ activeStats.driving.pending }}</td>
                    </tr>
                    <tr class="has-background-info-light">
                        <td>TBR</td>
                        <td>{{ activeStats.selfTbr.total }}</td>
                        <td>{{ activeStats.selfTbr.done }}</td>
                        <td>—</td>
                        <td>{{ activeStats.selfTbr.pending }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <th>Total</th>
                        <th>{{ activeStats.grandTotal.total }}</th>
                        <th>{{ activeStats.grandTotal.delivered }}</th>
                        <th>{{ activeStats.grandTotal.notDelivered }}</th>
                        <th>{{ activeStats.grandTotal.pending }}</th>
                    </tr>
                </tfoot>
            </table>
            </div>
        </template>

        <!-- Version 1: legacy fields (delivered, not_delivered, self_pickup) -->
        <template v-else-if="currentFD.statsVersion === 1">
            <table class="table is-fullwidth is-bordered is-striped">
                <tbody>
                    <tr>
                        <td><b>Total Deliveries</b></td>
                        <td>{{ currentFD.total }}</td>
                    </tr>
                    <tr>
                        <td><b>Self Collected</b></td>
                        <td>{{ currentFD.self_pickup }}</td>
                    </tr>
                    <tr>
                        <td><b>Delivered</b></td>
                        <td>{{ currentFD.delivered }}</td>
                    </tr>
                    <tr>
                        <td><b>Not Delivered</b></td>
                        <td>{{ currentFD.not_delivered }}</td>
                    </tr>
                    <tr>
                        <td><b>Pending</b></td>
                        <td>{{ currentFD.total - (currentFD.delivered + currentFD.self_pickup + currentFD.not_delivered) }}</td>
                    </tr>
                </tbody>
            </table>
        </template>

        <!-- Version 2 (no statsVersion): FD-doc counter fields -->
        <template v-else>
            <table class="table is-fullwidth is-bordered is-striped">
                <thead>
                    <tr>
                        <th>Delivery</th>
                        <th>Total</th>
                        <th>Done</th>
                        <th>Pending</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Self</td>
                        <td>{{ currentFD.total_self_collection }}</td>
                        <td>{{ currentFD.self_collected }}</td>
                        <td>{{ currentFD.total_self_collection - currentFD.self_collected }}</td>
                    </tr>
                    <tr>
                        <td>Walking</td>
                        <td>{{ currentFD.total_walking_deliveries }}</td>
                        <td>{{ currentFD.walking_delivered }}</td>
                        <td>{{ currentFD.total_walking_deliveries - currentFD.walking_delivered }}</td>
                    </tr>
                    <tr>
                        <td>Driving</td>
                        <td>{{ currentFD.total_driving_deliveries }}</td>
                        <td>{{ currentFD.driving_delivered }}</td>
                        <td>{{ currentFD.total_driving_deliveries - currentFD.driving_delivered }}</td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <th>Total</th>
                        <th>{{ currentFD.total }}</th>
                        <th>{{ currentFD.self_collected + currentFD.driving_delivered + currentFD.walking_delivered }}</th>
                        <th>{{ currentFD.total - (currentFD.self_collected + currentFD.driving_delivered + currentFD.walking_delivered) }}</th>
                    </tr>
                </tfoot>
            </table>
        </template>
    </div>

    <div class="block" v-if="currentFD.total_normal_bags_to_pack">
        <h1 class="title is-5">Bag Details</h1>
        <table class="table is-fullwidth is-bordered is-striped">
            <thead>
                <tr>
                    <th>Bag type</th>
                    <th>Count</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Total Normal Bags to Pack</td>
                    <td>{{ currentFD.total_normal_bags_to_pack }}</td>
                </tr>
                <tr>
                    <td>Total Dry Bags to Pack</td>
                    <td>{{ currentFD.total_dry_bags_to_pack }}</td>
                </tr>
                <tr>
                    <td>Total Normal Bags</td>
                    <td>{{ currentFD.total_normal_bags }}</td>
                </tr>
                <tr>
                    <td>Total Dry Bags</td>
                    <td>{{ currentFD.total_dry_bags }}</td>
                </tr>
                <tr>
                    <th>Total Bags</th>
                    <th>{{ currentFD.total_normal_bags + currentFD.total_dry_bags }}</th>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
export default {
    data() {
        return {
            tick: 0,
            tickInterval: null,
        };
    },
    computed: {
        currentFD() {
            return this.$store.getters['fd/getCurrentFD'];
        },
        deliveryStats() {
            return this.$store.getters['delivery/getDeliveryStats'];
        },
        deliveryStatsLoading() {
            return this.$store.getters['delivery/getDeliveryStatsLoading'];
        },
        activeStats() {
            if (this.currentFD.status === 'CLOSED') return this.currentFD.final_stats || null;
            return this.deliveryStats;
        },
        refreshCooldown() {
            void this.tick;
            const remaining = Math.ceil((this.$store.getters['delivery/getStatsRefreshCooldownUntil'] - Date.now()) / 1000);
            return Math.max(0, remaining);
        },
    },
    async mounted() {
        if (this.currentFD.statsVersion === 3 && this.currentFD.status !== 'CLOSED' && !this.deliveryStats && !this.deliveryStatsLoading) {
            await this.fetchStats();
        }
        this.tickInterval = setInterval(() => { this.tick++; }, 1000);
    },
    beforeUnmount() {
        clearInterval(this.tickInterval);
    },
    methods: {
        async fetchStats() {
            const fdID = this.currentFD?.id;
            if (!fdID) return;
            await this.$store.dispatch('delivery/fetchDeliveryStats', { fdID });
        },
        async refreshStats() {
            await this.fetchStats();
        },
    },
};
</script>
